// 20: "חיבור Gmail" — drawer זמני בצד ימין (אותה מעטפת ואנימציה של drawer הקונסנט), לא דף קבוע. ?mail=send|read|send,read
// כשאייבי/יצירה צריכים Gmail עכשיו (no_mailbox), ?mail=manage מהתפריט. כפתור אחד "התחבר עם Google" → חלון קופץ של גוגל;
// ה-callback מודיע (BroadcastChannel / postMessage) ונסגר, ה-drawer גם בודק את החשבון כל 2 שניות. הצלחה → "מחובר ✓",
// מודיע למי שנחסם (ריצה/תור — useOnMailConnected) ונסגר אחרי 1.5 שניות. חלון שנחסם → חזרה באותה לשונית עם נתיב חזרה.
// נסגר תמיד ב-X / Escape / לחיצה בחוץ.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../lib/api.ts';
import { hasCaps, mailApi, type MailAccount, type MailCap } from '../lib/mailApi.ts';
import {
  announceConnected,
  hasResumers,
  isMailMsg,
  MAIL_CHANNEL,
  MAIL_PARAM,
  MAIL_RESULT_PARAM,
  parseMailParam,
  returnPathFor,
  useMailConnect,
  type MailMode,
} from '../lib/mailConnect.ts';
import { T } from '../lib/strings.ts';
import { cn } from '../lib/utils.ts';
import { ErrorView, Loading } from './Status.tsx';
import { StatusLabel } from './ui/badge.tsx';
import { Button } from './ui/button.tsx';
import { Sheet } from './ui/sheet.tsx';
import { IconAlert, IconCheck, IconMail, IconX } from './brand/icons.tsx';

const CLOSE_AFTER_MS = 1500;
const POLL_MS = 2000;
const capsHe = (caps: MailCap[]) => caps.map((c) => T.mail.caps[c] ?? c).join(' + ');

export function MailConnectDrawer() {
  const [sp, setSp] = useSearchParams();
  const raw = sp.get(MAIL_PARAM);
  const mode = parseMailParam(raw);
  const result = sp.get(MAIL_RESULT_PARAM);
  const close = useCallback(
    () =>
      setSp((p) => {
        const n = new URLSearchParams(p);
        n.delete(MAIL_PARAM);
        n.delete(MAIL_RESULT_PARAM);
        return n;
      }),
    [setSp],
  );
  return (
    <Sheet open={!!mode} onClose={close} title={T.mail.title} side="start" className="max-w-md" testId="mail-drawer">
      <div className="flex items-center gap-2 border-b border-border p-3 ps-4">
        <IconMail size={20} className="text-primary" />
        <h2 className="m-0 me-auto text-lg font-bold">{T.mail.title}</h2>
        <Button variant="ghost" size="icon" aria-label={T.mail.close} title={T.mail.close} onClick={close} data-testid="mail-close">
          <IconX size={20} />
        </Button>
      </div>
      {mode && raw && <Body key={raw} mode={mode} modeParam={raw} result={result} onClose={close} />}
    </Sheet>
  );
}

type Phase = 'idle' | 'waiting' | 'done' | 'error';

function Body({ mode, modeParam, result, onClose }: { mode: MailMode; modeParam: string; result: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const loc = useLocation();
  const [phase, setPhaseState] = useState<Phase>(result ? (result === 'connected' ? 'done' : 'error') : 'idle');
  const phaseRef = useRef(phase);
  const setPhase = (p: Phase) => {
    phaseRef.current = p;
    setPhaseState(p);
  };
  const [errCode, setErrCode] = useState<string | null>(result && result !== 'connected' ? result : null);
  const [fault, setFault] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resuming, setResuming] = useState(() => hasResumers());
  const pending = useRef<MailCap[]>(mode.mode === 'need' ? mode.caps : ['send']);
  const popup = useRef<Window | null>(null);
  const q = useQuery({ queryKey: ['mail-account'], queryFn: mailApi.mine, staleTime: 0, refetchInterval: phase === 'waiting' ? POLL_MS : false });
  const a = q.data?.account ?? null;

  const succeed = useCallback(
    (caps: MailCap[]) => {
      if (phaseRef.current === 'done') return;
      setResuming(hasResumers());
      setPhase('done');
      void qc.invalidateQueries({ queryKey: ['mail-account'] });
      announceConnected(caps);
    },
    [qc],
  );

  // הודעה מהחלון של גוגל (דרך /mail/connected): BroadcastChannel + postMessage מאותו origin בלבד
  useEffect(() => {
    if (phase !== 'waiting') return;
    const on = (d: unknown) => {
      if (!isMailMsg(d)) return;
      if (d.ok) succeed(d.caps.length ? d.caps : pending.current);
      else {
        setErrCode(d.error);
        setPhase('error');
      }
    };
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel(MAIL_CHANNEL);
      ch.onmessage = (e) => on(e.data);
    } catch {
      ch = null;
    }
    const onMsg = (e: MessageEvent) => {
      if (e.origin === window.location.origin) on(e.data);
    };
    window.addEventListener('message', onMsg);
    return () => {
      ch?.close();
      window.removeEventListener('message', onMsg);
    };
  }, [phase, succeed]);

  // גיבוי: החשבון עצמו (COOP של גוגל מנתק את opener; BroadcastChannel לא בכל דפדפן)
  useEffect(() => {
    if (phase === 'waiting' && hasCaps(a, pending.current)) succeed(pending.current);
  }, [phase, a, succeed]);

  // חזרה באותה לשונית עם תוצאה מוצלחת — מודיעים פעם אחת
  useEffect(() => {
    if (result === 'connected') {
      setResuming(hasResumers());
      announceConnected(pending.current);
      void qc.invalidateQueries({ queryKey: ['mail-account'] });
    }
  }, []);

  // "מופיע ונעלם": אחרי הצלחה נסגר לבד
  useEffect(() => {
    if (phase !== 'done') return;
    const t = setTimeout(onClose, CLOSE_AFTER_MS);
    return () => clearTimeout(t);
  }, [phase, onClose]);

  const sameTab = async (caps: MailCap[]) => {
    const { url } = await mailApi.connectUrl(caps, { returnTo: returnPathFor(loc, modeParam) });
    window.location.assign(url);
  };

  const connect = async (caps: MailCap[]) => {
    setFault(null);
    setErrCode(null);
    setNotice(null);
    pending.current = caps;
    // נפתח מיד (בתוך הלחיצה — אחרת חוסם החלונות), ה-URL נטען אחרי שה-api מחזיר אותו
    const w = openPopup();
    try {
      if (!w) return void (await sameTab(caps));
      popup.current = w;
      setPhase('waiting');
      const { url } = await mailApi.connectUrl(caps, { popup: true });
      w.location.href = url;
    } catch (e) {
      w?.close();
      setFault(e instanceof ApiError ? e.textHe : T.network);
      setPhase('idle');
    }
  };

  const cancel = () => {
    try {
      popup.current?.close();
    } catch {
      /* חלון ממקור אחר */
    }
    setPhase('idle');
  };

  const disconnect = async () => {
    setBusy(true);
    setFault(null);
    try {
      await mailApi.disconnect();
      setNotice(T.mail.disconnected);
      await qc.invalidateQueries({ queryKey: ['mail-account'] });
    } catch (e) {
      setFault(e instanceof ApiError ? e.textHe : T.network);
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'done') return <Done address={a?.status === 'ok' ? a.address : null} resuming={resuming} />;
  if (q.isPending) return <div className="p-4"><Loading /></div>;
  if (q.isError) return <div className="p-4"><ErrorView error={q.error} onRetry={() => q.refetch()} /></div>;
  if (!q.data.configured)
    return (
      <p className="m-4 flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-sm" data-testid="mail-not-configured">
        <IconAlert size={18} className="mt-0.5 text-degraded" />
        {T.mail.notConfigured}
      </p>
    );

  const needCaps = mode.mode === 'need' ? mode.caps : [];
  const readOn = q.data.readEnabled; // 21 §3: gmail.readonly כבוי במופע עד שיוחלט — לא מציעים קריאה
  const reauth = a?.status === 'reauth_required' || a?.status === 'error';
  // צריך עכשיו ועוד לא מחובר → מסך החיבור; אחרת (מהתפריט / כבר מחובר) → מצב החשבון
  const showConnect = (mode.mode === 'need' && !hasCaps(a, needCaps)) || !a || reauth;
  const wanted: MailCap[] = mode.mode === 'need' ? needCaps : reauth && a?.caps.length ? a.caps : ['send'];
  const connectCaps = wanted.filter((c) => c !== 'read' || readOn);
  const readBlocked = connectCaps.length < wanted.length;
  const lead = reauth ? T.mail.reauth : mode.mode === 'need' ? (needCaps.length > 1 ? T.mail.needBoth : T.mail.need[needCaps[0]!]) : T.mail.intro;

  return (
    <div className="flex flex-col gap-4 p-4" data-testid="mail-body" data-phase={phase} data-mode={mode.mode}>
      {phase === 'error' && errCode && (
        <p role="alert" className="m-0 rounded-md border border-error/40 bg-error/5 p-3 text-sm" data-testid="mail-error">
          {T.mail.error(errCode)}
        </p>
      )}
      {fault && (
        <p role="alert" className="m-0 rounded-md border border-error/40 bg-error/5 p-3 text-sm" data-testid="mail-fault">
          {fault}
        </p>
      )}
      {notice && (
        <p role="status" className="m-0 rounded-md bg-muted p-3 text-sm" data-testid="mail-notice">
          {notice}
        </p>
      )}

      {showConnect ? (
        phase === 'waiting' ? (
          <div className="flex flex-col items-center gap-4 py-6 text-center" data-testid="mail-waiting" role="status">
            <span aria-hidden="true" className="size-10 animate-spin rounded-circle border-[3px] border-primary/25 border-t-primary" />
            <p className="m-0 font-semibold">{T.mail.waiting}</p>
            <p className="m-0 flex flex-wrap items-center justify-center gap-x-2 text-sm text-muted-foreground">
              {T.mail.noWindow}
              <button type="button" className="cursor-pointer font-medium text-primary underline underline-offset-2" onClick={() => void sameTab(pending.current).catch(() => setFault(T.network))} data-testid="mail-same-tab">
                {T.mail.sameTab}
              </button>
            </p>
            <Button variant="ghost" size="sm" onClick={cancel} data-testid="mail-cancel">
              {T.mail.cancel}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className={cn('grid size-11 shrink-0 place-items-center rounded-circle', reauth ? 'bg-degraded/10 text-degraded' : 'bg-primary-soft text-primary')}>
                {reauth ? <IconAlert size={22} /> : <IconMail size={22} />}
              </span>
              <p className="m-0 pt-2.5 text-[15px] leading-relaxed" data-testid="mail-lead">
                {lead}
              </p>
            </div>
            {readBlocked && (
              <p className="m-0 rounded-md bg-muted p-3 text-sm" data-testid="mail-read-off">
                {T.mail.readOff}
              </p>
            )}
            {connectCaps.length > 0 && (
              <Button className="min-h-12 w-full text-base" onClick={() => void connect(connectCaps)} data-testid="mail-google">
                {T.mail.google}
              </Button>
            )}
            <ConsentNote read={connectCaps.includes('read')} />
          </div>
        )
      ) : (
        <Account a={a!} readOn={readOn} busy={busy} needSatisfied={mode.mode === 'need'} onAdd={(c) => void connect([c])} onDisconnect={() => void disconnect()} waiting={phase === 'waiting'} />
      )}
    </div>
  );
}

/** 21 §2: מה מאשרים בדיוק + מדיניות הפרטיות — גוגל דורשת את זה ליד כפתור החיבור. */
function ConsentNote({ read }: { read: boolean }) {
  return (
    <p className="m-0 text-center text-sm leading-relaxed text-muted-foreground" data-testid="mail-consent-note">
      {read ? T.mail.consentNoteRead : T.mail.consentNote}{' '}
      <a href={T.mail.privacyUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-primary underline underline-offset-2" data-testid="mail-privacy-link">
        {T.mail.privacy}
      </a>
    </p>
  );
}

function Account({ a, readOn, busy, needSatisfied, waiting, onAdd, onDisconnect }: { a: MailAccount; readOn: boolean; busy: boolean; needSatisfied: boolean; waiting: boolean; onAdd: (c: MailCap) => void; onDisconnect: () => void }) {
  const missing = (['send', 'read'] as const).filter((c) => !a.caps.includes(c) && (c !== 'read' || readOn));
  return (
    <div className="flex flex-col gap-4" data-testid="mail-account" data-status={a.status}>
      <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3 shadow-card">
        <span aria-hidden="true" className="grid size-10 shrink-0 place-items-center rounded-circle bg-ok/10 text-ok">
          <IconCheck size={20} />
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <bdi dir="ltr" className="truncate text-start font-semibold" data-testid="mail-address">
            {a.address}
          </bdi>
          <span className="text-sm text-muted-foreground" data-testid="mail-caps">
            {T.mail.connectedFor(capsHe(a.caps))}
          </span>
        </div>
        <StatusLabel tone="ok" data-testid="mail-status">
          {T.mail.status[a.status] ?? a.status}
        </StatusLabel>
      </div>
      {needSatisfied && (
        <p role="status" className="m-0 text-sm text-muted-foreground">
          {T.mail.retry}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {missing.map((c) => (
          <Button key={c} variant="outline" size="sm" disabled={busy || waiting} onClick={() => onAdd(c)} data-testid={`mail-add-${c}`}>
            {waiting ? T.mail.waiting : T.mail.add[c]}
          </Button>
        ))}
        <Button variant="destructive" size="sm" className="ms-auto" disabled={busy} onClick={onDisconnect} data-testid="mail-disconnect">
          {busy ? T.mail.disconnecting : T.mail.disconnect}
        </Button>
      </div>
    </div>
  );
}

function Done({ address, resuming }: { address: string | null; resuming: boolean }) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-10 text-center" role="status" data-testid="mail-done">
      <span aria-hidden="true" className="grid size-14 place-items-center rounded-circle bg-ok/15 text-ok animate-[sheet-fade_200ms_ease-out]">
        <IconCheck size={30} />
      </span>
      <p className="m-0 text-lg font-bold">
        {T.mail.done} <span aria-hidden="true">✓</span>
      </p>
      {address && (
        <bdi dir="ltr" className="font-medium text-muted-foreground" data-testid="mail-done-address">
          {address}
        </bdi>
      )}
      <p className="m-0 text-sm text-muted-foreground" data-testid="mail-done-next">
        {resuming ? T.mail.resuming : T.mail.retry}
      </p>
    </div>
  );
}

/** חלון קופץ ממורכז מעל החלון הנוכחי; null = נחסם (אז חוזרים באותה לשונית). */
function openPopup(): Window | null {
  const w = 520;
  const h = 680;
  const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - w) / 2));
  const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - h) / 2));
  try {
    return window.open('about:blank', 'aibify-gmail', `popup=yes,width=${w},height=${h},left=${left},top=${top}`);
  } catch {
    return null;
  }
}

/**
 * כרטיס קטן בתמליל / בריצה שנעצרה: "צריך לחבר Gmail" + "חבר Gmail" (פותח את ה-drawer שוב); אחרי החיבור — "Gmail מחובר ✓".
 * המצב מהחשבון עצמו (אותו cache של ה-drawer), לא מהאירוע.
 */
export function MailConnectCard({ caps, className }: { caps: MailCap[]; className?: string }) {
  const open = useMailConnect();
  const q = useQuery({ queryKey: ['mail-account'], queryFn: mailApi.mine, staleTime: 5_000 });
  const ok = hasCaps(q.data?.account, caps);
  return (
    <div
      className={cn('flex flex-wrap items-center gap-2 rounded-md border p-2 ps-3 text-sm', ok ? 'border-ok/30 bg-ok/5' : 'border-primary/30 bg-primary-soft/50', className)}
      data-testid="mail-connect-card"
      data-connected={ok}
    >
      {ok ? <IconCheck size={18} className="text-ok" /> : <IconMail size={18} className="text-primary" />}
      <span className="me-auto font-medium">{ok ? T.mail.connectedCard : T.mail.needCard}</span>
      {ok ? (
        q.data?.account && (
          <bdi dir="ltr" className="text-muted-foreground">
            {q.data.account.address}
          </bdi>
        )
      ) : (
        <Button size="sm" onClick={() => open(caps)} data-testid="mail-connect-open">
          {T.mail.connect}
        </Button>
      )}
    </div>
  );
}
